import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import type { Studio } from '@/api/client';
import { ContextMenu } from '@/components/ui/ContextMenu/ContextMenu';
import type { ContextMenuItem } from '@/components/ui/ContextMenu/ContextMenu';
import { useCatalogManageOpener, useCanManageCatalog } from '@/contexts/CatalogManageOpenerContext';
import '@/components/ProjectsTable/EditableCell.css';

export type StudioCatalogField = 'name' | 'legal_entity' | 'notes';

export interface StudioEditableCellProps {
  studio: Studio;
  field: StudioCatalogField;
  children: ReactNode;
  disabled?: boolean;
}

export function StudioEditableCell({
  studio,
  field,
  children,
  disabled = false,
}: StudioEditableCellProps) {
  const openCatalogManage = useCatalogManageOpener();
  const canManageCatalog = useCanManageCatalog();
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [contextMenuPos, setContextMenuPos] = useState<{ x: number; y: number } | null>(null);
  const cellRef = useRef<HTMLDivElement>(null);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (disabled || !canManageCatalog) return;
      e.preventDefault();
      e.stopPropagation();
      setContextMenuPos({ x: e.clientX, y: e.clientY });
      setContextMenuOpen(true);
    },
    [disabled, canManageCatalog]
  );

  const handleEditFromMenu = useCallback(() => {
    setContextMenuOpen(false);
    if (field === 'legal_entity' && studio.legal_entity?.id) {
      openCatalogManage({
        tab: 'legal_entities',
        legalEntityId: studio.legal_entity.id,
      });
      return;
    }
    openCatalogManage({
      tab: 'studios',
      studioId: studio.id,
    });
  }, [field, openCatalogManage, studio.id, studio.legal_entity?.id]);

  const contextMenuItems = useMemo<ContextMenuItem[]>(
    () => (canManageCatalog ? [{ label: 'Редактировать', onClick: handleEditFromMenu }] : []),
    [canManageCatalog, handleEditFromMenu]
  );

  return (
    <div
      ref={cellRef}
      className={clsx('editable-cell', disabled && 'studios-table-catalog__editable-cell_disabled')}
      onContextMenu={handleContextMenu}
    >
      <div className="editable-cell__content">{children}</div>
      <ContextMenu
        isOpen={contextMenuOpen}
        onClose={() => setContextMenuOpen(false)}
        anchorPosition={contextMenuPos}
        items={contextMenuItems}
      />
    </div>
  );
}
